import { useMemo, useState } from 'react';
import { Box, Flex, type FlexProps } from '@chakra-ui/react';
import { Badge, Drawer, Popover, Tabs, Text } from '@kula-ai/components';
import { DocusignWordmarkAsset } from '@kula-ai/assets';
import { InfoCircle } from '@kula-ai/icons-core';
import { DocxViewer } from '@/components/file-viewers';
import {
  ESIGN_ACTIVITY_COPY,
  ESIGN_DRAWER_WIDTH_CLASS,
} from '@/constants/esignature';
import type { EsignWizardDocument } from '@/types/esignature';
import { colors } from '@/theme/foundations/colors';
import { formatToken, invalidTokensOf, validTokensOf } from './esignDocuments';

/**
 * The document's token count, and what those tokens are.
 *
 * The design shows a bare "Info 6" pill with nothing saying what 6 counts. Six
 * is exactly the number of DocuSign anchors a document may carry, and the offer
 * flow already uses this same pill shape for its token summary — so it is read
 * as the tokens found in the open document, and the popover names them rather
 * than leaving the number unexplained.
 */
const DocumentTokenInfo = ({
  document,
}: {
  document?: EsignWizardDocument;
}) => {
  const validTokens = validTokensOf(document?.validation);
  const invalidTokens = invalidTokensOf(document?.validation);
  const total = validTokens.length + invalidTokens.length;

  const trigger = (
    <Flex
      align="center"
      gap="6px"
      bg="secondary-50"
      p="3px 3px 3px 6px"
      borderRadius="6px"
      cursor="pointer"
      w="fit-content"
    >
      {/* Not a `--kula-colors-*` var: only seven exist and this is not one. */}
      <InfoCircle size={16} color={colors['secondary-900']} />
      <Text variant="h3">{ESIGN_ACTIVITY_COPY.TOKEN_INFO}</Text>
      <Badge type="outline" color="white">
        {String(total)}
      </Badge>
    </Flex>
  );

  if (total === 0) return trigger;

  return (
    <Popover trigger={trigger} openOn="hover" hoverDelay={150} side="bottom">
      {/* Bounded: an envelope can carry ~72 tokens, and without a ceiling the
          popover grew to fit them and spanned the viewport. */}
      <Flex
        flexDir="column"
        gap="8px"
        p="4px"
        minW="260px"
        maxW="420px"
        maxH="320px"
        overflowY="auto"
      >
        {invalidTokens.length > 0 && (
          <Flex flexDir="column" gap="6px">
            <Text variant="overhead">
              {ESIGN_ACTIVITY_COPY.INVALID_TOKENS_HEADING}
            </Text>
            <Flex flexWrap="wrap" gap="4px">
              {invalidTokens.map((token) => (
                <Box
                  key={token}
                  px="8px"
                  py="2px"
                  bg="pastel-1-light"
                  borderRadius="6px"
                >
                  <Text variant="p" color="error">
                    {formatToken(token)}
                  </Text>
                </Box>
              ))}
            </Flex>
          </Flex>
        )}
        {validTokens.length > 0 && (
          <Flex flexDir="column" gap="6px">
            <Text variant="overhead">
              {ESIGN_ACTIVITY_COPY.VALID_TOKENS_HEADING}
            </Text>
            <Flex flexWrap="wrap" gap="4px">
              {validTokens.map((token) => (
                <Box
                  key={token}
                  px="8px"
                  py="2px"
                  bg="secondary-50"
                  borderRadius="6px"
                >
                  <Text variant="p">{formatToken(token)}</Text>
                </Box>
              ))}
            </Flex>
          </Flex>
        )}
      </Flex>
    </Popover>
  );
};

const DocumentBody = ({ document }: { document?: EsignWizardDocument }) => {
  const url = document?.url;

  if (!url) {
    return (
      <Flex flex="1" h="100%" align="center" justify="center" p="48px">
        <Text color="subtle">{ESIGN_ACTIVITY_COPY.PREVIEW_UNAVAILABLE}</Text>
      </Flex>
    );
  }

  return (
    // Both `flex` and the explicit size: with several documents this is a cell in
    // the side-tabs GRID, where `h/w: 100%` is what fills it; with one it sits
    // directly in a flex ROW, where without `flex: 1` it shrinks to the
    // document's own width and leaves the rest of the drawer empty.
    <Box flex="1" h="100%" w="100%" minW={0} minH={0} bg="secondary-25">
      <DocxViewer
        url={url}
        filename={document?.filename}
        hideBorder
        toolbarLeadingSlot={<DocumentTokenInfo document={document} />}
      />
    </Box>
  );
};

/** Typed rather than inline — see the note on DocxViewerToolbar's BAR_STYLES. */
const TITLE_ROW: FlexProps = {
  align: 'center',
  justify: 'space-between',
  w: '100%',
};

export interface EsignDocumentPreviewProps {
  isOpen: boolean;
  onClose: () => void;
  documents: EsignWizardDocument[];
  /** Document to open on. Falls back to the first. */
  initialKey?: string;
}

/**
 * Read-only preview of the documents on an e-sign activity.
 *
 * With two or more documents the design puts a vertical file rail on the left
 * and the viewer beside it; with one there is no rail and the viewer takes the
 * full width. That is `Tabs` in its `side` variant, so the same component
 * covers both by simply not rendering when there is nothing to switch between.
 */
export const EsignDocumentPreview = ({
  isOpen,
  onClose,
  documents,
  initialKey,
}: EsignDocumentPreviewProps) => {
  const [activeKey, setActiveKey] = useState<string | undefined>(initialKey);

  // Reopening on a different row has to move the selection back to that row,
  // even though `activeKey` still holds a perfectly valid key from last time.
  // Adjusted during render rather than in an effect, so the drawer never paints
  // the previously-viewed document for a frame first.
  const [openedFor, setOpenedFor] = useState(initialKey);
  if (isOpen && openedFor !== initialKey) {
    setOpenedFor(initialKey);
    setActiveKey(initialKey);
  }

  // A removed document must not leave the drawer pointing at nothing.
  const selectedKey = useMemo(() => {
    const keys = documents.map((document) => document.key);
    if (activeKey && keys.includes(activeKey)) return activeKey;
    if (initialKey && keys.includes(initialKey)) return initialKey;
    return keys[0];
  }, [activeKey, initialKey, documents]);

  const selected = documents.find((document) => document.key === selectedKey);
  const hasRail = documents.length > 1;

  return (
    <Drawer
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      // `size` only offers viewport shares, so cap the 80% one: narrower
      // viewports still get 80% rather than an overflowing fixed width.
      size="lg"
      // `!mr-0` undoes kula's stacked-drawer inset. `useDrawerDepth` gives a
      // drawer opened from inside another one a 24px right margin so the parent
      // peeks out behind it — sensible as a default, and wrong here: the design
      // (95:14100) has this flush to the edge, and from the interview plan the
      // parent is a Chakra drawer so there is no depth and no inset, which made
      // the same component sit differently on the two surfaces.
      //
      // kula ships no prop to opt out of the inset — worth raising upstream.
      className={`!mr-0 ${ESIGN_DRAWER_WIDTH_CLASS}`}
      closeOnEscape
      title={
        <Flex {...TITLE_ROW}>
          <Text variant="h2">{ESIGN_ACTIVITY_COPY.PREVIEW_TITLE}</Text>
          <DocusignWordmarkAsset width={24} height={24} />
        </Flex>
      }
      headerClassName="!px-6 !py-4 border-b border-secondary-100"
      // The body's own `overflow-y-auto` would scroll the file rail along with
      // the document. Clamp it here so the document pane is the only scroller.
      bodyClassName="!p-0 !overflow-hidden"
    >
      <Flex h="100%" minH={0} data-test-id="esign-document-preview">
        {hasRail ? (
          <Tabs.Root
            variant="side"
            value={selectedKey}
            onValueChange={setActiveKey}
            // Figma 95:14635/95:14636: a 198px column, 16px of lead-in, 8px
            // side padding, and 36px rows 4px apart. The design also draws a
            // top border, which is dropped here — the drawer header already
            // lays a rule along that edge and two would stack.
            //
            // `self-stretch` overrides the variant's `self-start`, which would
            // hug the file list and leave the rule stopping short of the
            // bottom; the rail then scrolls on its own once the list is long.
            tabListWrapperClassName="w-[198px] shrink-0 self-stretch overflow-y-auto border-r border-secondary-100 pt-4"
            listClassName="gap-1 px-2"
            triggerClassName="h-9 gap-1.5 rounded-md py-3 pl-4 pr-3 data-[state=active]:bg-primary-50"
            // Only the document pane scrolls, so the panel fills its cell and
            // clips rather than growing the grid row.
            panelClassName="h-full min-h-0 overflow-hidden"
            // The variant is a two-column grid, not a flex row — forcing
            // `flex` here breaks the `col-start-*` placement of both cells. It
            // ships a 24px gutter; Figma butts the rail against the document.
            className="h-full min-h-0 w-full grid-rows-[minmax(0,1fr)] gap-0"
            items={documents.map((document) => ({
              value: document.key,
              // kula's side label wraps (`whitespace-normal`), so a long
              // filename grew past the 36px row and clipped mid-line. The
              // inner span carries the full name for the hover tooltip.
              label: <span title={document.filename}>{document.filename}</span>,
              // Spelled out rather than `truncate`: tailwind-merge does not
              // treat that shorthand as conflicting with the variant's
              // `whitespace-normal`, so both survive and the wrap wins.
              labelClassName: 'overflow-hidden text-ellipsis whitespace-nowrap',
              panel: <DocumentBody document={document} />,
            }))}
          />
        ) : (
          <DocumentBody document={selected} />
        )}
      </Flex>
    </Drawer>
  );
};
